import { XMarkIcon } from '@heroicons/react/20/solid'
import { request } from '../../utils/request';
import { api } from '../../utils/api';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { Jobs, LocksmithList, LocksmithStats, PaginationTableColumns } from '../../utils/types';
import { useParams } from 'react-router-dom';
import LocksmithNotFound from '../../components/locksmith-not-found';
import parsePhoneNumberFromString, { parsePhoneNumberWithError } from 'libphonenumber-js';
import PaginationTable from '../../components/pagination-table';
import moment from 'moment';
import clsx from 'clsx';
import { JobStatus } from '../../utils/enum';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import Complain from '../../components/complain';
import JobTableMobile, { JobTableMobileSkeleton } from '../../components/job-table-mobile';
import Dropdown from '../../components/dropdown';
import { getHHMMSS } from '../../utils/helpers';

const dropdown = [
    { id: 1, value: 'All' },
    { id: 2, value: 'this week' },
    { id: 3, value: 'this month' }
]

export default function LocksmithDetail() {
    const params = useParams();
    const id = Number(params.id);
    const isValidId = !Number.isNaN(id)
    const [details, setDetails] = useState<LocksmithList>();
    const [selectedJob, setSelectedJob] = useState<Jobs>();
    const [open, setOpen] = useState(false);
    const [reload, setReload] = useState(Date.now());
    const [range, setRange] = useState<typeof dropdown[0]>({ id: 1, value: 'All' });
    const [loading, setLoading] = useState<boolean>(true);
    useEffect(() => {
        if (isValidId)
            fetchLocksmithDetail(id, range.id === 2 ? 'week' : range.id === 3 ? 'month' : undefined).then((data) => {
                setLoading(false)
                setDetails(data);
            }).catch(() => {
                setLoading(false)
            });
    }, [id, reload, isValidId, range])

    const stats = useMemo(() => {
        if (!details?.stats) return [];
        const stat = details?.stats as LocksmithStats;
        const list = [
            { name: 'Total clocked in time', value: getHHMMSS(stat.total_clocked_in_duration) },
            { name: 'Total complain count', value: stat.total_complain_count },
            { name: 'Total job cancellations', value: stat.total_job_cancellations },
            { name: 'Total job completed', value: stat.total_jobs_completed },
            { name: 'Total work time', value: getHHMMSS(stat.total_work_time) },
            { name: 'Total complain Rate', value: ((stat.total_complain_count / stat.total_jobs_completed) * 100).toFixed(2) + '%' },
        ]
        if (range.id !== 1) {
            // @ts-ignore
            list.push({ name: 'Available days ' + range.value, value: stat.available_days ?? '-' })
            list.push(
                { name: 'Job cancellations ' + range.value, value: stat.range_job_cancellations },
                { name: 'Job completed ' + range.value, value: stat.range_job_completed },
                { name: 'Work time ' + range.value, value: getHHMMSS(stat.range_work_time) },
                { name: 'Complain Rate ' + range.value, value: ((stat.range_complain_count / stat.range_job_completed) * 100).toFixed(2) + '%' },
            )
        }
        return list
    }, [details, range])

    const columns: PaginationTableColumns[] = useMemo(() => [
        {
            header: 'Job Id',
            identifier: ['id'],
            cell(row) {
                return <p>#{row.id}</p>
            },
        },
        {
            header: 'Customer Name',
            identifier: ['customer_name'],
        },
        {
            header: 'Customer Address',
            identifier: ['customer_address'],
        },
        {
            header: 'Customer Phone No.',
            identifier: ['customer_contact_number'],
            cell(row) {
                return <p>{parsePhoneNumberFromString(row.customer_contact_number)?.formatInternational()}</p>
            },
        },
        {
            header: 'Status',
            identifier: ['job_status'],
            cell(row) {
                return <span className={clsx("inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset", getBadgeStatusColorClass(row.job_status))}>
                    {row.job_status?.toUpperCase?.()}
                </span>
            },
        },
        {
            header: 'Created At',
            identifier: ['created_at'],
            cell(row) {
                return <p>{moment(row.created_at).format('lll')}</p>
            },
        },
    ], [])

    const onClickRow = (e: any, row: any) => {
        setSelectedJob(row);
        setOpen(true)
    }

    return (
        (!isValidId || (!loading && !details)) ? <LocksmithNotFound />
            : loading
                ? <Shimmer />
                : details ? <div>
                    <div className="px-4 sm:px-0">
                        <h3 className="text-lg font-semibold text-gray-900">Locksmith Information</h3>
                    </div>
                    <div className="mt-2 border-t border-gray-100">
                        <dl className="divide-y divide-gray-100">
                            <div className="px-4 py-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                                <dt className="text-sm/6 font-medium text-gray-900">Full name</dt>
                                <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0">{details.full_name}</dd>
                            </div>
                            <div className="px-4 py-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                                <dt className="text-sm/6 font-medium text-gray-900">Email</dt>
                                <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0">{details.email}</dd>
                            </div>
                            <div className="px-4 py-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                                <dt className="text-sm/6 font-medium text-gray-900">Phone No.</dt>
                                <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0">{parsePhoneNumberWithError(details.locksmith.contact_number).formatInternational()}</dd>
                            </div>
                        </dl>
                        <div className='flex justify-end'>
                            <div className="w-full mb-2 md:w-1/4">
                                <span className="text-sm">Select Range for Jobs and Stats</span>
                                <Dropdown
                                    selected={range}
                                    onChange={setRange}
                                    data={dropdown}
                                />
                            </div>
                        </div>
                        <Stats stats={stats} />
                        <h3 className="mt-5 text-base/7 font-semibold text-gray-900">Jobs</h3>
                        <div className="flex w-full mt-2">
                            <PaginationTable
                                data={details.locksmith.jobs}
                                mobileShimmer={<JobTableMobileSkeleton />}
                                mobileView={<JobTableMobile data={details.locksmith.jobs} onPressRow={onClickRow} loading={loading} />}
                                loading={loading}
                                columns={columns}
                                onClickRow={onClickRow}
                            />
                        </div>
                    </div>
                    <JobDetail open={open} setOpen={setOpen} job={selectedJob} onChangeState={() => { setReload(Date.now()) }} />
                </div> : <></>
    )
}

function Shimmer() {
    return (
        <div className="animate-pulse space-y-4 mt-2">
            <div className="bg-gray-200 h-6 rounded w-3/4"></div>
            <div className="bg-gray-200 h-6 rounded w-1/2"></div>
            <div className="bg-gray-200 h-6 rounded w-full"></div>
        </div>
    );
}

async function fetchLocksmithDetail(id: number, range?: 'week' | 'month') {
    const query = new URLSearchParams();
    if (range === 'month') {
        query.append('start_date', moment().subtract(1, 'month').toISOString())
        query.append('end_date', moment().toISOString())
    }
    if (range === 'week') {
        query.append('start_date', moment().subtract(1, 'w').toISOString())
        query.append('end_date', moment().toISOString())
    }
    const res = await request('GET', api.locksmith.detail + id + '?' + query.toString())
    return res.data?.data;
}

interface JobDetailProps {
    open: boolean;
    setOpen: Dispatch<SetStateAction<boolean>>
    job?: Jobs;
    onChangeState?: (job: Jobs) => void
}

function JobDetail({ job, ...props }: JobDetailProps) {
    return (
        <Dialog open={props.open} onClose={props.setOpen} className="relative z-10">
            <DialogBackdrop
                transition
                className="fixed inset-0 bg-gray-500/75 transition-opacity duration-300 ease-in-out data-[closed]:opacity-0"
            />
            <div className="fixed inset-0" />

            <div className="fixed inset-0 overflow-hidden">
                <div className="absolute inset-0 overflow-hidden">
                    <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
                        <DialogPanel
                            transition
                            className="pointer-events-auto w-screen max-w-md transform transition duration-300 ease-in-out data-[closed]:translate-x-full sm:duration-500"
                        >
                            {job ? <div className="flex h-full flex-col overflow-y-scroll bg-white py-2 shadow-xl scrollbar-none">
                                <div className="px-4 sm:px-6 mt-20 lg:mt-4">
                                    <div className="flex items-start justify-between">
                                        <DialogTitle className="text-base font-semibold text-gray-900">Job (#{job.id}) Details</DialogTitle>
                                        <div className="ml-3 flex h-7 items-center">
                                            <button
                                                type="button"
                                                onClick={() => props.setOpen(false)}
                                                className="relative rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                                            >
                                                <span className="absolute -inset-2.5" />
                                                <span className="sr-only">Close panel</span>
                                                <XMarkIcon aria-hidden="true" className="size-6" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <div className="relative flex-1 px-2">
                                    <div>
                                        <div className="mt-6 border-t border-gray-100">
                                            <dl className="divide-y divide-gray-100">
                                                <div className="px-4 py-2">
                                                    <dt className="text-sm/6 font-medium text-gray-900">Status</dt>
                                                    <dd className="text-sm/6 text-gray-700">
                                                        <span className={clsx("inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset", getBadgeStatusColorClass(job.job_status))}>
                                                            {job.job_status.toUpperCase()}
                                                        </span>
                                                    </dd>
                                                </div>
                                                <div className="px-4 py-2">
                                                    <dt className="text-sm/6 font-medium text-gray-900">Customer name</dt>
                                                    <dd className="text-sm/6 text-gray-700">{job.customer_name}</dd>
                                                </div>
                                                <div className="px-4 py-2">
                                                    <dt className="text-sm/6 font-medium text-gray-900">Customer Address</dt>
                                                    <dd className="text-sm/6 text-gray-700">{job.customer_address}</dd>
                                                </div>
                                                <div className="px-4 py-2">
                                                    <dt className="text-sm/6 font-medium text-gray-900">Customer Phone</dt>
                                                    <dd className="text-sm/6 text-gray-700">{job.customer_contact_number}</dd>
                                                </div>
                                                <div className="px-4 py-2">
                                                    <dt className="text-sm/6 font-medium text-gray-900">Job Issue</dt>
                                                    <dd className="text-sm/6 text-gray-700">{job.job_issue}</dd>
                                                </div>
                                                {job.job_notes && <div className="px-4 py-2">
                                                    <dt className="text-sm/6 font-medium text-gray-900">Job Notes by Tech</dt>
                                                    <dd className="text-sm/6 text-gray-700">{job.job_notes}</dd>
                                                </div>}
                                                {job.reject_reason && <div className="px-4 py-2">
                                                    <dt className="text-sm/6 font-medium text-gray-900">Reject Reason</dt>
                                                    <dd className="text-sm/6 text-gray-700">{job.reject_reason}</dd>
                                                </div>}
                                                <div className="px-4 py-2">
                                                    <dt className="text-sm/6 font-medium text-gray-900">{'Assigned At'}</dt>
                                                    <dd className="text-sm/6 text-gray-700">{moment(job.created_at).format('lll')}</dd>
                                                </div>
                                                {job.job_status !== JobStatus.PENDING && <div className="px-4 py-2">
                                                    <dt className="text-sm/6 font-medium text-gray-900">{job.job_status === JobStatus.REJECTED ? 'Rejected At' : 'Accepted At'}</dt>
                                                    <dd className="text-sm/6 text-gray-700">{moment(job.accepted_rejected_at).format('lll')}</dd>
                                                </div>}
                                                {job.job_status === JobStatus.COMPLETED && <div className="px-4 py-2">
                                                    <dt className="text-sm/6 font-medium text-gray-900">{'Completed At'}</dt>
                                                    <dd className="text-sm/6 text-gray-700">{moment(job.completed_at).format('lll')}</dd>
                                                </div>}
                                                {(job.job_status === JobStatus.COMPLETED && !job.complains) && <div className="px-4 py-2">
                                                    <Complain id={job.id} onRaised={(complains) => {
                                                        job.complains = { ...complains, job_id: job.id, id: 0, created_at: new Date().toISOString(), updated_at: new Date().toISOString() };
                                                        props.onChangeState?.(job)
                                                    }} />
                                                </div>}
                                                {job.complains && <>
                                                    <div className="px-4 py-2">
                                                        <dt className="text-sm/6 font-medium text-gray-900">Complain Title</dt>
                                                        <dd className="text-sm/6 text-gray-700">{job.complains?.complain_title}</dd>
                                                    </div>
                                                    <div className="px-4 py-2">
                                                        <dt className="text-sm/6 font-medium text-gray-900">Complain Description</dt>
                                                        <dd className="text-sm/6 text-gray-700">{job.complains?.complain_description}</dd>
                                                    </div>
                                                    <div className="px-4 py-2">
                                                        <dt className="text-sm/6 font-medium text-gray-900">Complain raised at</dt>
                                                        <dd className="text-sm/6 text-gray-700">{moment(job.complains?.created_at).format('lll')}</dd>
                                                    </div>
                                                </>}
                                            </dl>
                                        </div>
                                    </div>
                                </div>
                            </div> : null}
                        </DialogPanel>
                    </div>
                </div>
            </div>
        </Dialog>
    )
}

const getBadgeStatusColorClass = (job_status: JobStatus) => {
    if (job_status === JobStatus.PENDING) return 'bg-yellow-50 text-yellow-700 ring-yellow-600/20'
    if (job_status === JobStatus.REJECTED) return 'bg-red-50 text-red-700 ring-red-600/20'
    if (job_status !== JobStatus.ACCEPTED) return 'bg-green-50 text-green-700 ring-green-600/20'
    return 'bg-gray-50 text-gray-700 ring-gray-600/20'
}

interface StatsProps {
    stats: { name: string, value: number | string }[]
}

function Stats({ stats }: StatsProps) {
    return (
        <div>
            <h3 className="text-base font-semibold text-gray-900">Stats</h3>
            <div className='bg-gray-50 p-5 mt-2 rounded-lg'>
                <dl className="mt-5 grid grid-cols-1 divide-y divide-gray-200 overflow-hidden rounded-lg bg-white shadow md:grid-cols-3 md:divide-x md:divide-y-0">
                    {stats.map((item) => (
                        <div key={item.name} className="px-4 py-5 sm:p-6">
                            <dt className="font-normal text-gray-900">{item.name}</dt>
                            <dd className="mt-1 flex items-baseline justify-between md:block lg:flex">
                                <div className="flex items-baseline text-2xl font-semibold text-blue-600">
                                    {item.value}
                                </div>
                            </dd>
                        </div>
                    ))}
                </dl>
            </div>
        </div>
    )
}
